import map from "lodash/map";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import DropdownIcons from "../../../../parts/inputs/dropdown-icons";
import SvgIcon from "../../../../parts/SvgIcon";
import CycleCardCP from "./CycleCardCP";

const sortByName = (a, b) =>
	(a.name || "").localeCompare(b.name || "", undefined, {
		sensitivity: "base",
	});
const sortByNameDesc = (a, b) =>
	(b.name || "").localeCompare(a.name || "", undefined, {
		sensitivity: "base",
	});
const sortByCreatedDate = (a, b) => {
	const dateA = new Date(a.createdDate || a.createdAt || 0).getTime();
	const dateB = new Date(b.createdDate || b.createdAt || 0).getTime();
	return dateB - dateA;
};

const SORT_FUNCTIONS = {
	name: sortByName,
	nameDescending: sortByNameDesc,
	createdDate: sortByCreatedDate,
};

const WorkoutFolderDetail = ({ folder, workouts = [], locals, query = {} }) => {
	const { t } = useTranslation("coaching-portal");
	const sort = query.sort || "createdDate";
	const sortFn = SORT_FUNCTIONS[sort] || sortByCreatedDate;
	const sortedWorkouts = [...workouts].sort(sortFn);
	const { activeUserToRequest } = locals;

	const sortItems = [
		{
			title: t("allTemplates.header.dateAdded"),
			src: "/img/svg/arrow-to-bottom.svg",
			alt: "createdDate",
			className: "icon-white",
			id: "createdDate",
		},
		{
			title: t("allTemplates.header.az"),
			src: "/img/svg/sort-amount-up.svg",
			alt: "name",
			className: "icon-white",
			id: "name",
		},
		{
			title: t("allTemplates.header.za"),
			src: "/img/svg/sort-amount-down.svg",
			alt: "nameDescending",
			className: "icon-white",
			id: "nameDescending",
		},
	];

	const goBack = () => {
		submit("/actions/refresh", {
			method: "GET",
			body: {
				folderId: "",
				tabView: "single-workouts-tab",
				templateView: "single-workouts",
			},
		});
	};

	const openManage = () => {
		submit("/actions/refresh", {
			method: "GET",
			body: {
				"modal[type]": "manage-workout-folder",
				targetFolderId: folder._id,
			},
		});
	};

	const addWorkoutUrl = `/actions/refresh/?modal[type]=build-step-one&cycleType=new&isTemplate=true&storeType=single-workout&fromTemplate=true&tabView=single-workouts-tab&templateView=single-workouts&folderId=${folder._id}`;

	return (
		<section className="workout-folder-detail">
			<header className="workout-folder-detail-header">
				<div className="workout-folder-detail-info">
					<button
						type="button"
						className="workout-folder-detail-back"
						onClick={goBack}
						aria-label={t("allTemplates.workoutFolders.back", "Back")}
					>
						<SvgIcon src="/img/new-icons/back-button.svg" />
					</button>
					<SvgIcon
						src="/img/new-icons/folder.svg"
						classNames="workout-folder-detail-icon"
					/>
					<div className="workout-folder-detail-text">
						<h3>{folder.name}</h3>
						<p>
							{t("allTemplates.workoutFolders.detailMeta", {
								count: sortedWorkouts.length,
								description: folder.description || "",
								defaultValue_one: "{{count}} Workout | {{description}}",
								defaultValue_other: "{{count}} Workouts | {{description}}",
							})}
						</p>
					</div>
				</div>
				<div className="workout-folder-detail-actions">
					<DropdownIcons
						title={t("allTemplates.header.sort")}
						items={sortItems}
						action={(nextSort) => {
							submit("/actions/refresh", {
								method: "GET",
								body: { sort: nextSort, folderId: folder._id },
							});
						}}
					/>
					<button type="button" data-button-type="outline" onClick={openManage}>
						<SvgIcon
							src="/img/new-icons/edit-pencil-gradient.svg"
							noFilterOnLight={true}
						/>
						{t("allTemplates.workoutFolders.manageWorkout", "Manage Workout")}
					</button>
					<a
						className="force-mobile-hidden"
						role="button"
						data-button-type="primary"
						href={addWorkoutUrl}
					>
						<img src="/img/new-icons/add.svg" alt="" />
						{t("allTemplates.workoutFolders.addWorkout", "Add Workout")}
					</a>
				</div>
			</header>

			<div className="columns" id="drag-items">
				{sortedWorkouts.length > 0 ? (
					map(sortedWorkouts, (cycle, cardIndex) => (
						<CycleCardCP
							key={cycle._id}
							cycle={cycle}
							cardIndex={cardIndex}
							isOwnCycle={cycle.owner === activeUserToRequest._id}
						/>
					))
				) : (
					<div className="workout-folder-detail-empty">
						<p>
							{t(
								"allTemplates.workoutFolders.folderEmpty",
								"No workouts in this folder yet.",
							)}
						</p>
						<button
							type="button"
							data-button-type="primary"
							onClick={openManage}
						>
							{t(
								"allTemplates.workoutFolders.manageWorkouts",
								"Manage Workouts",
							)}
						</button>
					</div>
				)}
			</div>
		</section>
	);
};

WorkoutFolderDetail.propTypes = {
	folder: PropTypes.shape({
		_id: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		description: PropTypes.string,
		workoutIds: PropTypes.array,
	}).isRequired,
	workouts: PropTypes.array,
	locals: PropTypes.object.isRequired,
	query: PropTypes.object,
};

export default WorkoutFolderDetail;
